/**
 * Inline error renderer for the standard service error envelope (task 21.2).
 *
 * Every service returns the shared envelope `{ error: { code, message, field,
 * requestId } }`, decoded by the API client into an {@link ApiError}. This
 * component renders the non-sensitive `message` in an assertive live region so
 * assistive tech announces failures (R9.7), and exposes the machine-readable
 * `code`/`requestId` as data attributes for support/debugging. Non-`ApiError`
 * values render a generic fallback so a thrown non-envelope never leaks detail.
 */

import { isApiError } from '../auth/index.js';

/** Extract a user-facing, non-sensitive message from any caught value. */
function messageOf(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }
  return 'Something went wrong. Please try again.';
}

/** Render `null` when there is no error, else the envelope message inline. */
export function ErrorNotice({ error }: { readonly error: unknown }): JSX.Element | null {
  if (error === undefined || error === null) {
    return null;
  }
  const code = isApiError(error) ? error.code : undefined;
  const requestId = isApiError(error) ? error.requestId : undefined;
  return (
    <p
      role="alert"
      className="error-notice"
      data-error-code={code}
      data-request-id={requestId}
    >
      {messageOf(error)}
    </p>
  );
}

/**
 * If the given error is an envelope error attributed to `field`, return its
 * message; otherwise `undefined`. Used to render validation errors (R3.9, R5.8)
 * inline next to the specific input the service flagged.
 */
export function fieldErrorMessage(error: unknown, field: string): string | undefined {
  if (isApiError(error) && error.field === field) {
    return error.message;
  }
  return undefined;
}
